import type { ReactNode } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import ArticleHero from '../../../../components/news/ArticleHero';
import PullQuote from '../../../../components/news/PullQuote';
import KeyPointsBox from '../../../../components/news/KeyPointsBox';
import CommentBox from '../../../../components/news/CommentBox';
import SourceCard from '../../../../components/news/SourceCard';
import RelatedCards from '../../../../components/news/RelatedCards';

/* ---- In-article inline parts (editorial layout; no section bands) ---- */
function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}
function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        Source: {media} (<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>)
      </figcaption>
    </figure>
  );
}
function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}
const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function JumeirahBurjAlArab25thAnniversaryPageEN() {
  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'News', href: '/en/news' },
    { label: 'Jumeirah Burj Al Arab 25th Anniversary' }
  ];

  const article = {
    category: 'Luxury Hospitality & Commemorative Events',
    date: 'November 29, 2024',
    title: 'Jumeirah Burj Al Arab 25th Anniversary',
    subtitle: "25 Years of Continuous Butler Service at World's Most Luxurious Hotel",
    heroImage: '/images/generated/jba-fs-096-news_jumeirah-burj-al-arab-25th-annivers.jpg',
    lead: 'Jumeirah, a member of Dubai Holding, celebrated the 25th anniversary of Jumeirah Burj Al Arab on December 1, 2024. Since opening in 1999, this hotel has redefined luxury hospitality in the UAE, demonstrating the strategic significance of providing butler service in all suites. Twenty-five years of innovative events and butler service excellence have established global luxury hotel standards.',
    sections: [
      {
        heading: '25th Anniversary and Brand Evolution',
        image: '/images/generated/jba-fs-097-news_jumeirah-burj-al-arab-25th-annivers-v3.jpg', aspect: 'aspect-[3/4]',
        alt: 'The iconic dhow-sail architecture of Jumeirah Burj Al Arab',
        paragraphs: [
          'Since opening in 1999, Jumeirah Burj Al Arab has become a globally recognized symbol of excellence in tourism as a landmark shaped like an iconic dhow sail. The 25th anniversary is an important milestone for the Jumeirah brand, focusing on sustainable growth after announcing a new visual identity in early 2024.',
          'Jumeirah continues to expand regionally and internationally in gateway cities and resort destinations while reaffirming its position as an industry-changing leader in luxury hospitality. In line with this growth strategy, new properties have been announced including Jumeirah Red Sea (Saudi Arabia), Jumeirah Marsa Al Arab (UAE), and Jumeirah Le Richemond Geneva (Switzerland).'
        ]
      },
      {
        heading: 'Fusion of Arabian Cultural Hospitality and Butler Services',
        image: '/images/generated/jba-fs-098-news_jumeirah-burj-al-arab-25th-annivers-v3.jpg', aspect: 'aspect-[3/4]',
        alt: 'Arabian cultural hospitality inspired by warmth and generosity',
        paragraphs: [
          'COO and Interim CEO Thomas Meier comments, "As we celebrate 25 years since opening the iconic Jumeirah Burj Al Arab, we reflect on a legacy of excellence in luxury hospitality inspired by the warmth and generosity of Arabian culture."',
          "This tradition of welcoming travelers and sharing culture and conversation is at the heart of Jumeirah's vision. By creating not just beautiful destinations but spaces that connect the world, properties remain global majlis (gathering places), providing opportunities for unique cultural connections with purpose."
        ]
      },
      {
        heading: '25 Years of Memorable Events',
        image: '/images/generated/jba-fs-099-news_jumeirah-burj-al-arab-25th-annivers-v3.jpg', aspect: 'aspect-[3/4]',
        alt: 'Dazzling events that defy gravity at the luxury hotel',
        paragraphs: [
          "For 25 years, Jumeirah Burj Al Arab has captivated the world with dazzling events and awe-inspiring stunts that defy expectations, imagination, and gravity itself. As bold challenges on the helipad, in 2013 Skydive Dubai's His Excellency Nasser Al Neyadi successfully made the first skydiving landing on the helipad.",
          'In 2005, Andre Agassi and Roger Federer played a tennis match above the clouds, and in 2017, Anthony Joshua and Seb Eubank faced off in the sky. In 2021, David Guetta performed a DJ set as part of the "United at Home" series supporting UNICEF and Dubai Cares, raising funds for students and teachers affected by the pandemic.'
        ]
      },
      {
        heading: 'Butler Service Excellence and Brand Identity',
        image: '/images/generated/jba-fs-100-news_jumeirah-burj-al-arab-25th-annivers-v3.jpg', aspect: 'aspect-video',
        alt: 'Personalized butler service provided in every suite',
        paragraphs: [
          'Jumeirah Burj Al Arab continues to set unprecedented standards of personalized luxury. Providing butler service in all suites and equipped with a fleet of bespoke Rolls Royce vehicles, this strategic significance clearly demonstrates the indispensability of butler services in luxury hospitality.',
          'At Skyview Bar, the once world\'s most expensive cocktail "27.321" (inspired by the bar\'s location on the 27th floor at 321 meters height) was offered for 27,321 dirhams, embodying the value of butler services as core to brand identity.'
        ]
      },
      {
        heading: 'Social Contribution and Sustainability Initiatives',
        image: '/images/generated/jba-fs-101-news_jumeirah-burj-al-arab-25th-annivers-v4.jpg', aspect: 'aspect-[3/4]',
        alt: 'A marine conservation project rescuing endangered turtles',
        paragraphs: [
          "Beyond eye-catching luxury, Jumeirah Burj Al Arab is central to Jumeirah's ongoing commitment to being a good neighbor and protecting the places it calls home. The Dubai Turtle Rehabilitation Project (DTRP) is housed at this iconic hotel, functioning as part of Jumeirah's ongoing marine conservation efforts.",
          'Through this project, thousands of endangered turtles have been rescued, rehabilitated, and returned to the wild, fulfilling social responsibility as a luxury hotel while extending butler service values of "spirit of service" to environmental protection.'
        ]
      }
    ],
    comment: 'The 25th anniversary of Jumeirah Burj Al Arab is an important milestone clearly proving the value demonstrated by 25 years of continuous butler service at the world\'s most luxurious hotel, and our association deeply respects this achievement. The strategic significance of providing butler service in all suites and the indispensability of butler services in luxury hospitality demonstrate to the world the value of butler services as core to brand identity. The unique approach of fusing Arabian cultural hospitality with butler services suggests possibilities for service development leveraging cultural backgrounds, which is also very valuable as a suggestion for Japan\'s luxury hotel industry. Referencing such international success stories, we will actively work toward creating new value through the fusion of Japan\'s unique "omotenashi" culture and butler services.',
    source: [
      { label: 'Source', value: 'Jumeirah Official Press Release', link: false },
      { label: 'Article URL', value: 'https://www.jumeirah.com/en/jumeirah-group/press-centre/press-releases/press-releases-for-2024/25-years-of-redefining-luxury-celebrating-the-legacy-of-jumeirah-burj-al-arab', link: true },
      { label: 'Access Date', value: 'October 2025', link: false }
    ],
    related: [
      { to: '/en/news/raffles-london-campaign', image: '/images/generated/jba-fs-en-016-news_jumeirah-burj-al-arab-25th-anni.jpg', aspect: 'aspect-[3/4]', alt: 'Related article: Raffles "The Butler Did It" Campaign Chapter 2', title: 'Raffles "The Butler Did It" Campaign Chapter 2', desc: 'Luxury hotel butler service innovation' },
      { to: '/en/news/ai-hospitality-revolution', image: '/images/generated/jba-fs-en-017-news_jumeirah-burj-al-arab-25th-anni.jpg', aspect: 'aspect-[3/4]', alt: 'Related article: AI Hospitality Revolution: 5 Transformations', title: 'AI Hospitality Revolution: 5 Transformations', desc: 'The future of butler services opened by AI' }
    ]
  };
  const S = article.sections;

  const sources = [
    { label: 'Source', value: 'Jumeirah Official Press Release', link: false },
    { label: 'Landmark Facts', value: 'https://en.wikipedia.org/wiki/Burj_Al_Arab', link: true },
    { label: '25th Anniversary (official)', value: 'https://mediahub.jumeirah.com/jumeirah-burj-al-arab-introduces-a-limited-edition-chocolate-to-mark-its-25th-anniversary', link: true },
    { label: '25th Anniversary Coverage', value: 'https://globalloyalty.org/article/16669/25-years-of-redefining-luxury-celebrating-the-legacy-of-jumeirah-burj-al-arab', link: true },
    { label: 'Access Date', value: 'July 2026', link: false }
  ];
  const toc = [
    { id: 's-anniversary', label: '25th Anniversary and Brand Evolution' },
    { id: 's-facts', label: 'Burj Al Arab Landmark Facts' },
    { id: 's-culture', label: 'Fusion of Arabian Cultural Hospitality and Butler Services' },
    { id: 's-events', label: '25 Years of Memorable Events' },
    { id: 's-butler', label: 'Butler Service Excellence and Brand Identity' },
    { id: 's-legacy', label: '25th Anniversary Initiatives and Legacy' },
    { id: 's-social', label: 'Social Contribution and Sustainability Initiatives' }
  ];

  return (
    <Layout>
      <title>Jumeirah Burj Al Arab 25th Anniversary | Japan Butler Association</title>
      <meta name="description" content="Jumeirah celebrates the 25th anniversary of Jumeirah Burj Al Arab, redefining luxury hospitality in the UAE with butler service in all suites and 25 years of iconic events." />
      <link rel="canonical" href="https://butler-association.jp/en/news/jumeirah-burj-al-arab-25th-anniversary" />
      <link rel="alternate" hrefLang="ja" href="https://butler-association.jp/news/jumeirah-burj-al-arab-25th-anniversary" />
      <link rel="alternate" hrefLang="en" href="https://butler-association.jp/en/news/jumeirah-burj-al-arab-25th-anniversary" />

      <ArticleHero eyebrow="News / Luxury" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <p className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{article.lead}</p>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="Article Highlights"
              points={[
                'Jumeirah celebrated the 25th anniversary of Jumeirah Burj Al Arab on December 1, 2024',
                'Opened in 1999 and standing 321m, the all-suite hotel (202 rooms) provides butler service in every suite',
                '25 years of innovative events and butler service excellence have set global luxury hotel standards'
              ]}
            />

            <SectionHeading id="s-anniversary" en="Anniversary">{S[0].heading}</SectionHeading>
            <div className="space-y-6">{S[0].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-facts" en="Landmark Facts">Burj Al Arab Landmark Facts</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Burj Al Arab opened on <Stat>December 1, 1999</Stat> as a landmark shaped like a dhow's sail, standing <Stat>321 metres</Stat> tall (top floor 197.5 m). It is composed entirely of suites, numbering <Stat>202 rooms</Stat>.</p>
              <p className={P}>Its star rating is the top five stars from Forbes Travel Guide and Dubai's tourism authority. Often described as "seven-star," this is a journalist-coined nickname, not an official designation used by operator Jumeirah.</p>
            </div>
            <SourceShot
              src="/images/news-sources/jumeirah-wikipedia.jpg"
              alt="Screenshot of the English Wikipedia page listing the opening year, height and room count of Burj Al Arab"
              media="Wikipedia (Burj Al Arab)"
              url="https://en.wikipedia.org/wiki/Burj_Al_Arab"
            />

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="We reflect on a legacy of excellence in luxury hospitality inspired by the warmth and generosity of Arabian culture." cite="Thomas Meier / Jumeirah COO & Interim CEO" />
            </div>

            <SectionHeading id="s-culture" en="Culture">{S[1].heading}</SectionHeading>
            <div className="space-y-6">{S[1].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-events" en="Iconic Events">{S[2].heading}</SectionHeading>
            <div className="space-y-6">{S[2].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-butler" en="Butler Service">{S[3].heading}</SectionHeading>
            <div className="space-y-6">{S[3].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-legacy" en="25th Anniversary">25th Anniversary Initiatives and Legacy</SectionHeading>
            <div className="space-y-6">
              <p className={P}>On its 25th anniversary, <Stat>December 1, 2024</Stat>, Jumeirah launched a limited-edition chocolate bar (AED 175) and ran a feature revisiting "25 iconic moments."</p>
              <p className={P}>The anniversary announcements emphasized "refinement and preservation for the next 25 years," framed in the context of a roughly 18-month major restoration programme planned from April 2026. A quarter-century legacy of butler service in every suite is being handed to the next era.</p>
            </div>
            <SourceShot
              src="/images/news-sources/jumeirah-mediahub.jpg"
              alt="Screenshot of the Jumeirah official media hub page announcing a limited-edition chocolate for the 25th anniversary"
              media="Jumeirah Media Hub (official)"
              url="https://mediahub.jumeirah.com/jumeirah-burj-al-arab-introduces-a-limited-edition-chocolate-to-mark-its-25th-anniversary"
            />

            <SectionHeading id="s-social" en="Sustainability">{S[4].heading}</SectionHeading>
            <div className="space-y-6">{S[4].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="Contents" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">Opened</dt><dd className="text-[#333] mt-0.5">December 1, 1999</dd></div>
                  <div><dt className="text-[#8f7a56]">Height</dt><dd className="text-[#333] mt-0.5">321m</dd></div>
                  <div><dt className="text-[#8f7a56]">Rooms</dt><dd className="text-[#333] mt-0.5">202 all-suite</dd></div>
                  <div><dt className="text-[#8f7a56]">Rating</dt><dd className="text-[#333] mt-0.5">5 stars (Forbes)</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="Comment from Japan Butler Association" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="Source" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="Related Articles" items={article.related} readLabel="Read" backHref="/en/news" backLabel="Back to News List" />
    </Layout>
  );
}
